import { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode, Dispatch, SetStateAction } from "react";
import { historyList, Conversation, fetchAgents as fetchAgentsApi, AgentApiDto, getUserInfo, UserInfo, getAuthStatus, getAssistantTypeApi, CheckAuthResponse, OpenAiModel, SearchIndex } from "../../api";
import type { UserRole } from "../../api/models";

export interface ShellAgent {
  agentId: string;
  name: string;
  description: string;
  iconName: string;
  iconColor: string;
  route: string;
  remoteUrl: string | null;
  tags: string[];
  publisher: string;
  hasAccess: boolean;
}

export type AuthGateStatus = "loading" | "ok" | "configure-auth" | "unauthorized" | "access-denied" | "backend-failed";

interface ShellContextValue {
  agents: ShellAgent[];
  activeAgent: ShellAgent | null;
  setActiveAgentById: (agentId: string) => void;
  chatHistory: Conversation[];
  setChatHistory: Dispatch<SetStateAction<Conversation[]>>;
  refreshChatHistory: () => Promise<void>;
  userInfo: UserInfo | undefined;
  userRole: UserRole;
  userName: string | undefined;
  isAdmin: boolean;
  roleLoaded: boolean;
  canManagePrompts: boolean;
  setUserRole: (role: UserRole) => void;
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  sidebarWidth: number;
  setSidebarWidth: (width: number) => void;
  loading: boolean;
  settingsPanelOpen: boolean;
  setSettingsPanelOpen: Dispatch<SetStateAction<boolean>>;
  currentConversationId: string | null;
  setCurrentConversationId: (id: string | null) => void;
  onRateConversation: (() => void) | null;
  setOnRateConversation: (cb: (() => void) | null) => void;
  onNewChat: (() => void) | null;
  setOnNewChat: (cb: (() => void) | null) => void;
  pendingRateConvId: string | null;
  setPendingRateConvId: (id: string | null) => void;
  authGateStatus: AuthGateStatus;
  availableModels: OpenAiModel[];
  availableIndexes: SearchIndex[];
  assistantType: string;
  isAssistantAPILoading: boolean;
}

/** Product Knowledge Base — the home page, not listed as an agent in the store/sidebar. */
export const PKB_AGENT_ID = "a1000000-0000-0000-0000-000000000001";

// PKB is the home page — excluded from the agents list displayed in sidebar/store
const BUILTIN_AGENTS: ShellAgent[] = [
  {
    agentId: "a1000000-0000-0000-0000-000000000002",
    name: "RFP Intelligence Platform",
    description: "Automates the analysis and response generation for RFP documents. Extracts key requirement...",
    iconName: "ClipboardTask",
    iconColor: "#004b87",
    route: "rfp-intelligence",
    remoteUrl: null,
    tags: ["RFP", "Proposals", "Documents"],
    publisher: "Diebold Nixdorf",
    hasAccess: true,
  }
];

const ShellContext = createContext<ShellContextValue | null>(null);

const ICON_COLORS: Record<string, string> = {
  DocumentBulletList: "#004b87",
  DataBarVertical: "#6b21a8",
  Chat: "#0891b2",
  Shield: "#16a34a",
  Headset: "#dc2626",
  TrendingUp: "#e11d48",
  Money: "#16a34a",
  DocumentText: "#7c3aed",
  Settings: "#ea580c",
};

function mapApiAgent(dto: AgentApiDto): ShellAgent {
  const builtin = BUILTIN_AGENTS.find((a) => a.agentId === dto.agentId);
  return {
    agentId: dto.agentId,
    name: dto.name,
    description: dto.description ?? "",
    iconName: builtin?.iconName ?? dto.iconName,
    iconColor: builtin?.iconColor ?? ICON_COLORS[dto.iconName] ?? "#004b87",
    route: dto.route,
    remoteUrl: dto.remoteUrl ?? null,
    tags: builtin?.tags ?? [],
    publisher: builtin?.publisher ?? "Diebold Nixdorf",
    hasAccess: dto.hasAccess ?? true,
  };
}

export function useShell(): ShellContextValue {
  const ctx = useContext(ShellContext);
  if (!ctx) throw new Error("useShell must be used within ShellProvider");
  return ctx;
}

const OFFSET_INCREMENT = 25;

export function ShellProvider({ children }: { children: ReactNode }) {
  const [agents, setAgents] = useState<ShellAgent[]>(BUILTIN_AGENTS);
  const [activeAgentId, setActiveAgentId] = useState<string | null>(null);
  const [chatHistory, setChatHistory] = useState<Conversation[]>([]);
  const [userInfo, setUserInfo] = useState<UserInfo | undefined>();
  const [userRole, setUserRole] = useState<UserRole>("User");
  const [userName, setUserName] = useState<string | undefined>();
  const [roleLoaded, setRoleLoaded] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 768);
  const [sidebarWidth, setSidebarWidthState] = useState<number>(() => {
    const stored = localStorage.getItem("shell.sidebarWidth");
    const parsed = stored ? parseInt(stored, 10) : NaN;
    return isNaN(parsed) ? 280 : Math.min(480, Math.max(200, parsed));
  });
  const setSidebarWidth = useCallback((width: number) => {
    const clamped = Math.min(480, Math.max(200, width));
    setSidebarWidthState(clamped);
    localStorage.setItem("shell.sidebarWidth", String(clamped));
  }, []);
  const [loading, setLoading] = useState(false);
  const [settingsPanelOpen, setSettingsPanelOpen] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [onRateConversation, setOnRateConversation] = useState<(() => void) | null>(null);
  const [onNewChat, setOnNewChat] = useState<(() => void) | null>(null);
  const [pendingRateConvId, setPendingRateConvId] = useState<string | null>(null);
  const [authGateStatus, setAuthGateStatus] = useState<AuthGateStatus>("loading");
  const [availableModels, setAvailableModels] = useState<OpenAiModel[]>([]);
  const [availableIndexes, setAvailableIndexes] = useState<SearchIndex[]>([]);
  const [assistantType, setAssistantType] = useState<string>("");
  const [isAssistantAPILoading, setIsAssistantAPILoading] = useState(true);
  // Cache the auth promise so Layout and other consumers never trigger a second call.
  const authPromiseRef = useRef<Promise<{ userInfo: UserInfo | undefined; authStatus: CheckAuthResponse }> | null>(null);

  const isAdmin = userRole === "Administrator";
  const canManagePrompts = userRole === "Administrator" || userRole === "ProductManager";

  const activeAgent = agents.find((a) => a.agentId === activeAgentId) ?? null;

  const setActiveAgentById = useCallback((agentId: string) => {
    setActiveAgentId(agentId);
  }, []);

  // Load agents from backend on mount, fallback to BUILTIN_AGENTS
  useEffect(() => {
    let cancelled = false;
    fetchAgentsApi()
      .then((dtos) => {
        if (!cancelled && dtos.length > 0) {
          setAgents(dtos.map(mapApiAgent));
        }
      })
      .catch((e) => {
        console.warn("Failed to fetch agents from API, using built-in defaults:", e);
      });
    return () => { cancelled = true; };
  }, []);

  const refreshChatHistory = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    try {
      const response = await historyList(0);
      if (Array.isArray(response)) {
        setChatHistory(response);
        setOffset(response.length >= OFFSET_INCREMENT ? OFFSET_INCREMENT : 0);
        setHasMore(response.length >= OFFSET_INCREMENT);
      } else {
        setChatHistory([]);
      }
    } catch (e) {
      console.error("Failed to load chat history:", e);
      setChatHistory([]);
    } finally {
      setLoading(false);
    }
  }, [loading]);

  // Load chat history on mount
  useEffect(() => {
    refreshChatHistory();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Consolidated auth + assistant-type load on mount.
  // A single getUserInfo → getAuthStatus chain runs once; the promise is cached
  // in authPromiseRef so Layout (and any other consumer) can await the same result
  // without issuing duplicate network requests.
  useEffect(() => {
    const authPromise = (async () => {
      const info = await getUserInfo().catch(() => undefined);
      const email = info?.clientPrincipal?.claims?.find(
        (c: { typ: string; val: string }) => c.typ === "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"
      )?.val ?? info?.clientPrincipal?.userDetails;
      const auth = await getAuthStatus(email ?? "unknown").catch((): CheckAuthResponse => ({
        backendConnectionFailed: true,
        isAuthEnforced: false,
        isSignedIn: false,
        hasAccessPermission: false,
        availableModels: [],
        availableIndexes: [],
      }));
      return { userInfo: info, authStatus: auth };
    })();
    authPromiseRef.current = authPromise;

    // Run auth check and assistant-type fetch in parallel to cut cold-start latency.
    const assistantPromise = getAssistantTypeApi().catch(() => null);

    Promise.all([authPromise, assistantPromise]).then(([{ userInfo: info, authStatus: auth }, assistantResult]) => {
      // --- Auth state ---
      if (info) setUserInfo(info);
      if (auth.userRole) setUserRole(auth.userRole);
      if (auth.userName) setUserName(auth.userName);
      setRoleLoaded(true);

      // --- Auth gate (consumed by Layout) ---
      if (auth.backendConnectionFailed) {
        setAuthGateStatus("backend-failed");
      } else if (auth.isAuthEnforced && !info) {
        setAuthGateStatus("configure-auth");
      } else if (!auth.isSignedIn) {
        setAuthGateStatus("unauthorized");
      } else if (!auth.hasAccessPermission) {
        setAuthGateStatus("access-denied");
      } else {
        setAuthGateStatus("ok");
      }

      // --- Models & indexes (previously only in Layout) ---
      if (auth.availableModels?.length > 0) setAvailableModels(auth.availableModels);
      if (auth.availableIndexes?.length > 0) setAvailableIndexes(auth.availableIndexes);

      // --- Assistant type (previously only in Chat) ---
      if (assistantResult) setAssistantType(assistantResult.ai_assistant_type);
      setIsAssistantAPILoading(false);
    }).catch(() => {
      setRoleLoaded(true);
      setAuthGateStatus("backend-failed");
      setIsAssistantAPILoading(false);
    });
  }, []);

  return (
    <ShellContext.Provider
      value={{
        agents,
        activeAgent,
        setActiveAgentById,
        chatHistory,
        setChatHistory,
        refreshChatHistory,
        userInfo,
        userRole,
        userName,
        isAdmin,
        roleLoaded,
        canManagePrompts,
        setUserRole,
        sidebarOpen,
        setSidebarOpen,
        sidebarWidth,
        setSidebarWidth,
        loading,
        settingsPanelOpen,
        setSettingsPanelOpen,
        currentConversationId,
        setCurrentConversationId,
        onRateConversation,
        setOnRateConversation,
        onNewChat,
        setOnNewChat,
        pendingRateConvId,
        setPendingRateConvId,
        authGateStatus,
        availableModels,
        availableIndexes,
        assistantType,
        isAssistantAPILoading,
      }}
    >
      {children}
    </ShellContext.Provider>
  );
}
